import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { api, formatCurrencyFromCents, formatDate, formatTimeOfDay, formatStatus } from '../../utils/api'
import CustomerPayoutCard from './CustomerPayoutCard'
import DealLink from '../shared/DealLink.jsx'

/**
 * One customer's record: who they are, every deal they have had with us, and
 * what they have agreed to be emailed about.
 *
 * A page rather than a modal, because it is somewhere you land from a search,
 * a link in a ticket, or an address bar, and somewhere you should be able to
 * send a colleague.
 */
//: The editable contact fields, in the order they read on the page. Order is
//: name, then how to reach them, then where they are.
const CONTACT_FIELDS = [
  { name: 'first_name', label: 'First name' },
  { name: 'last_name', label: 'Last name' },
  { name: 'email', label: 'Email', type: 'email' },
  { name: 'phone', label: 'Phone', type: 'tel' },
  { name: 'address_line', label: 'Address' },
  { name: 'city', label: 'City' },
  { name: 'postcode', label: 'Postcode' },
]

/** The deal reference, linked to the deal. All Deals opens it by either id. */
function dealReference(quote) {
  return <DealLink dealId={quote.hubspot_dealid || quote.quote_id} reference={quote.reference_code} />
}

/** The deal's value, or a dash. Shared so the table and the stacked list on a
 *  phone cannot drift into formatting the same number two ways. */
function dealValue(quote) {
  return quote.amount_cents != null
    ? formatCurrencyFromCents(quote.amount_cents, quote.currency)
    : '-'
}

function CustomerDetail() {
  const { customerId } = useParams()
  const navigate = useNavigate()

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [savingPreference, setSavingPreference] = useState(false)
  const [preferenceMessage, setPreferenceMessage] = useState(null)

  // Its own request: it fans out over their quotes and calls Postmark, so
  // folding it into the main read would make the whole page wait for it.
  const [timeline, setTimeline] = useState(null)
  const [timelineError, setTimelineError] = useState(null)

  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState({})
  const [savingContact, setSavingContact] = useState(false)
  const [contactError, setContactError] = useState(null)

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError(null)
      try {
        const result = await api.get(`/api/customers/${encodeURIComponent(customerId)}`)
        if (!cancelled) setData(result)
      } catch (e) {
        if (!cancelled) setError(e.message || 'Could not load this customer')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [customerId])

  useEffect(() => {
    let cancelled = false
    async function loadTimeline() {
      setTimeline(null)
      setTimelineError(null)
      try {
        const result = await api.get(`/api/customers/${encodeURIComponent(customerId)}/timeline`)
        if (!cancelled) setTimeline(result)
      } catch (e) {
        if (!cancelled) setTimelineError(e.message || 'Could not load this history')
      }
    }
    loadTimeline()
    return () => { cancelled = true }
  }, [customerId])

  function startEditing() {
    const current = data?.customer?.pii || {}
    setDraft(Object.fromEntries(CONTACT_FIELDS.map(f => [f.name, current[f.name] ?? ''])))
    setContactError(null)
    setEditing(true)
  }

  async function saveContact(event) {
    event.preventDefault()
    setSavingContact(true)
    setContactError(null)

    const current = data?.customer?.pii || {}
    // Only what actually moved. Sending the whole form would make every save a
    // write to every field, filling the audit trail with non-changes and
    // letting a stale form revert an edit somebody else just made.
    const changes = {}
    for (const field of CONTACT_FIELDS) {
      const typed = (draft[field.name] ?? '').trim()
      // Empty means clear, which the API takes as an explicit null.
      const next = typed === '' ? null : typed
      if ((current[field.name] ?? null) !== next) changes[field.name] = next
    }

    if (Object.keys(changes).length === 0) {
      setEditing(false)
      setSavingContact(false)
      return
    }

    try {
      const result = await api.patch(
        `/api/customers/${encodeURIComponent(customerId)}`,
        changes
      )
      setData(prev => ({ ...prev, customer: result.customer }))
      setEditing(false)
    } catch (e) {
      setContactError({
        message: e.message || 'Could not save these details',
        otherCustomerId: e.body?.conflict?.customer_id || null,
      })
    } finally {
      setSavingContact(false)
    }
  }

  async function toggleReminders(optOut) {
    setSavingPreference(true)
    setPreferenceMessage(null)
    try {
      const result = await api.patch(
        `/api/customers/${encodeURIComponent(customerId)}/preferences`,
        { reminders_opt_out: optOut }
      )
      setData(prev => ({ ...prev, preferences: result.preferences }))
      setPreferenceMessage(
        optOut
          ? 'Saved. They will not get reminder emails.'
          : 'Saved. Reminder emails are back on.'
      )
    } catch (e) {
      // The switch is rendered from state, so a failed save leaves it showing
      // the truth rather than the change we did not manage to make.
      setPreferenceMessage(e.message || 'That did not save. Try again.')
    } finally {
      setSavingPreference(false)
    }
  }

  if (loading) {
    return <div className="card" style={{ padding: 'var(--space-4)' }}>Loading customer…</div>
  }

  if (error) {
    return (
      <div>
        <button type="button" className="btn btn-secondary" onClick={() => navigate('/customers')}>
          Back to search
        </button>
        <div className="card" style={{ padding: 'var(--space-4)', marginTop: 'var(--space-3)', color: 'var(--color-danger)' }}>
          {error}
        </div>
      </div>
    )
  }

  const customer = data?.customer || {}
  const pii = customer.pii || {}
  const quotes = data?.quotes
  const preferences = data?.preferences
  const name = [pii.first_name, pii.last_name].filter(Boolean).join(' ') || '(no name on record)'

  return (
    <div>
      <button type="button" className="btn btn-secondary" onClick={() => navigate('/customers')}>
        Back to search
      </button>

      <div className="page-header" style={{ marginTop: 'var(--space-3)' }}>
        <h1 className="page-title">{name}</h1>
        <p className="page-description">{pii.email || 'No email address on record'}</p>
      </div>

      <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
          <h2 style={{ marginTop: 0 }}>Contact details</h2>
          {!editing && (
            <button type="button" className="btn btn-secondary" onClick={startEditing}>Edit</button>
          )}
        </div>

        {!editing && (
          <table className="table">
            <tbody>
              <tr><th style={{ width: '30%' }}>Name</th><td>{name}</td></tr>
              <tr><th>Email</th><td>{pii.email || '-'}</td></tr>
              <tr><th>Phone</th><td>{pii.phone || '-'}</td></tr>
              <tr><th>Address</th><td>{pii.address_line || '-'}</td></tr>
              <tr><th>City</th><td>{pii.city || '-'}</td></tr>
              <tr><th>Postcode</th><td>{pii.postcode || '-'}</td></tr>
              <tr>
                <th>Customer since</th>
                <td>{customer.created_at ? formatDate(customer.created_at) : '-'}</td>
              </tr>
            </tbody>
          </table>
        )}

        {editing && (
          <form onSubmit={saveContact}>
            <table className="table">
              <tbody>
                {CONTACT_FIELDS.map(field => (
                  <tr key={field.name}>
                    <th style={{ width: '30%' }}>
                      <label htmlFor={`contact-${field.name}`}>{field.label}</label>
                    </th>
                    <td>
                      <input
                        id={`contact-${field.name}`}
                        className="form-input"
                        type={field.type || 'text'}
                        value={draft[field.name] ?? ''}
                        disabled={savingContact}
                        onChange={e => setDraft(prev => ({ ...prev, [field.name]: e.target.value }))}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {contactError && (
              <p style={{ color: 'var(--color-danger)' }}>
                {contactError.message}
                {/* A clash is a merge, not a typo. Point at the other record so
                    somebody can go and look rather than guess. */}
                {contactError.otherCustomerId && (
                  <>
                    {' '}
                    <Link to={`/customers/${contactError.otherCustomerId}`}>
                      Open that customer
                    </Link>
                  </>
                )}
              </p>
            )}

            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
              <button type="submit" className="btn btn-primary" disabled={savingContact}>
                {savingContact ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={savingContact}
                onClick={() => { setEditing(false); setContactError(null) }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>

      <CustomerPayoutCard customerId={customerId} />

      <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
        <h2 style={{ marginTop: 0 }}>Email settings</h2>
        {preferences ? (
          <>
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <input
                type="checkbox"
                checked={!preferences.reminders_opt_out}
                disabled={savingPreference}
                onChange={e => toggleReminders(!e.target.checked)}
              />
              <span>Send reminder emails</span>
            </label>
            <p style={{ color: 'var(--color-text-muted)', marginTop: 'var(--space-2)' }}>
              Chase-ups and expiring-quote nudges. Quotes, collections and
              payment confirmations always send, and there is no switch for
              them.
            </p>
            {preferences.updated_at && (
              <p style={{ color: 'var(--color-text-muted)' }}>
                {/* Who, not just what kind. "by operator" tells a reader nothing
                    they can act on; whether the customer or a colleague changed
                    it is the interesting part, and which colleague. Rows written
                    before migration 0317 have no name, and say so rather than
                    inventing one. */}
                Last changed {formatDate(preferences.updated_at)} by{' '}
                {preferences.updated_by === 'customer'
                  ? 'the customer'
                  : (preferences.updated_by_user || `${preferences.updated_by} (not recorded)`)}.
              </p>
            )}
            {!preferences.updated_at && (
              <p style={{ color: 'var(--color-text-muted)' }}>
                They have never changed this, so they are on the default.
              </p>
            )}
            {preferenceMessage && <p><strong>{preferenceMessage}</strong></p>}
          </>
        ) : (
          <p style={{ color: 'var(--color-danger)' }}>
            Could not load email settings. The rest of this page is still accurate.
          </p>
        )}
      </div>

      <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
        <h2 style={{ marginTop: 0 }}>Email history</h2>

        {!timeline && !timelineError && <p>Loading history…</p>}

        {timelineError && <p style={{ color: 'var(--color-danger)' }}>{timelineError}</p>}

        {timeline && (
          <>
            {/* What this list cannot see, said up front. A history that quietly
                omits a channel gets trusted as complete. */}
            <p style={{ color: 'var(--color-text-muted)' }}>
              Emails we sent them, their support conversations, and what
              happened to their deals. Shopify order emails and Klaviyo
              campaigns are not included.
              {timeline.truncated && ` Showing their ${timeline.quotes_scanned} most recent deals.`}
            </p>

            {!timeline.emails_available && (
              <p style={{ color: 'var(--color-danger)' }}>
                Could not read the email record, so sent emails are missing from this list.
              </p>
            )}
            {!timeline.events_available && (
              <p style={{ color: 'var(--color-danger)' }}>
                Could not load deal activity, so it is missing from this list.
              </p>
            )}
            {timeline.support_available === false && (
              <p style={{ color: 'var(--color-danger)' }}>
                Could not read the support inbox, so those messages are missing.
              </p>
            )}

            {timeline.entries.length === 0 && timeline.emails_available && timeline.events_available && (
              <p>Nothing yet. We have never emailed them and nothing has happened to their deals.</p>
            )}

            {timeline.entries.length > 0 && (
              <>
                {/* Five columns do not fit a phone. Rather than leave the last
                    one off the edge of a scroll nobody discovers, the same rows
                    render as a stacked list below tablet width. */}
                <table className="table history-table">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Date</th>
                      <th>What</th>
                      <th>Detail</th>
                      <th>Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {timeline.entries.map((entry, i) => (
                      <tr key={`${entry.at}-${i}`}>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatTimeOfDay(entry.at)}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatDate(entry.at)}</td>
                        <td>{entry.title}</td>
                        <td>
                          {/* A subject you can open beats a subject you then have
                              to go and find in the inbox. */}
                          {entry.link
                            ? <Link to={entry.link}>{entry.detail || 'Open the message'}</Link>
                            : (entry.detail || '-')}
                        </td>
                        <td style={{ color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                          {entry.source || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <ul className="history-stack">
                  {timeline.entries.map((entry, i) => (
                    <li key={`stack-${entry.at}-${i}`}>
                      <div className="history-stack__when">
                        {formatTimeOfDay(entry.at)} · {formatDate(entry.at)}
                        <span className="history-stack__source"> · {entry.source || 'Unknown'}</span>
                      </div>
                      <div className="history-stack__what">{entry.title}</div>
                      {(entry.detail || entry.link) && (
                        <div className="history-stack__detail">
                          {entry.link
                            ? <Link to={entry.link}>{entry.detail || 'Open the message'}</Link>
                            : entry.detail}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}
      </div>

      <div className="card">
        <h2 style={{ marginTop: 0 }}>
          Deal history {Array.isArray(quotes) ? `(${quotes.length})` : ''}
        </h2>
        {!Array.isArray(quotes) && (
          <p style={{ color: 'var(--color-danger)' }}>
            Could not load this customer's deals.
          </p>
        )}
        {Array.isArray(quotes) && quotes.length === 0 && (
          <p>No deals yet. They exist as a customer, but have not sold us anything.</p>
        )}
        {Array.isArray(quotes) && quotes.length > 0 && (
          <>
            {/* Six columns, same problem as the history above: on a phone the
                Value fell off the edge. Stacked below tablet width. */}
            <table className="table history-table">
              <thead>
                <tr>
                  <th>Reference</th>
                  <th>Title</th>
                  <th>Kind</th>
                  <th>Stage</th>
                  <th>Value</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {quotes.map(quote => (
                  <tr key={quote.quote_id}>
                    <td>{dealReference(quote)}</td>
                    <td>{quote.title || quote.reference_code}</td>
                    <td>{formatStatus(quote.kind)}</td>
                    <td>{formatStatus(quote.stage)}</td>
                    <td>{dealValue(quote)}</td>
                    <td>{formatDate(quote.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <ul className="history-stack">
              {quotes.map(quote => (
                <li key={`stack-${quote.quote_id}`}>
                  <div className="history-stack__when">
                    {formatDate(quote.created_at)}
                    <span className="history-stack__source">
                      {' · '}{formatStatus(quote.kind)} · {formatStatus(quote.stage)}
                    </span>
                  </div>
                  {/* Value leads on mobile: it is the number somebody is
                      scanning for, and it is the one that fell off the edge. */}
                  <div className="history-stack__what">
                    {dealReference(quote)} — {dealValue(quote)}
                  </div>
                  {quote.title && quote.title !== quote.reference_code && (
                    <div className="history-stack__detail">{quote.title}</div>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  )
}

export default CustomerDetail
